import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { Dialog } from '@headlessui/react';
import { importCSVFile } from '../services/csvImport';
import { useToast } from '../contexts/ToastContext';
import { CategoryItem, DestinationItem, ResponsibleItem, Transaction } from '../types/budget';
import { useI18n } from '../hooks/useI18n.ts';
import i18n from '../i18n';

interface ImportCSVButtonProps {
  showText?: boolean;
  className?: string;
  onImport: (
    fileName: string,
    transactions: Transaction[],
    categories: CategoryItem[],
    responsibles: ResponsibleItem[],
    destinations: DestinationItem[]
  ) => void;
}

// Helper function to normalize responsible names (copied from csvImport.ts to ensure consistency)
const normalizeResponsible = (responsible: string): string => {
  const lowerResponsible = responsible.toLowerCase();
  const languages = i18n.languages || [];
  const commonTranslations = languages.map((lang) =>
    i18n.getFixedT(lang)('defaults.responsibles.common').toLowerCase()
  );
  commonTranslations.push('common');
  if (commonTranslations.includes(lowerResponsible)) {
    return 'common';
  }
  return responsible;
};

export const ImportCSVButton: React.FC<ImportCSVButtonProps> = ({
  showText = false,
  className = '',
  onImport,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const { defaults } = useI18n();

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const transactions = await importCSVFile(file);

      // Start with default categories and responsibles
      const categories: CategoryItem[] = [];
      const responsibles: ResponsibleItem[] = [...defaults.responsibles];
      const destinations: DestinationItem[] = [...defaults.destinations];

      // Collect new categories and responsibles from transactions
      transactions.forEach((transaction) => {
        // Check and add new categories
        if (transaction.category) {
          const exists = categories.some(
            (cat) => cat.name.toLowerCase() === transaction.category.toLowerCase()
          );

          if (!exists) {
            categories.push({
              id: transaction.category.toLowerCase().replace(/\s+/g, '-'),
              name: transaction.category,
            });
          }
        }

        // Check and add new responsibles
        if (transaction.responsible) {
          // Normalize the responsible name from the transaction
          const normalizedResponsible = normalizeResponsible(transaction.responsible);
          const exists = responsibles.some((resp) => {
            // Compare using normalized names
            return normalizeResponsible(resp.name) === normalizedResponsible;
          });

          if (!exists && normalizedResponsible !== 'common') {
            responsibles.push({
              id: transaction.responsible.toLowerCase().replace(/\s+/g, '-'),
              name: transaction.responsible,
            });
          }
        }

        // Check and add new destinations
        if (transaction.destination) {
          const exists = destinations.some(
            (dest) => dest.name.toLowerCase() === transaction.destination.toLowerCase()
          );

          if (!exists) {
            destinations.push({
              id: transaction.destination.toLowerCase().replace(/\s+/g, '-'),
              name: transaction.destination,
            });
          }
        }
      });

      const fileName = file.name
        .replace('.csv', '')
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      // Show success toast notification
      showToast(t('budget.importSuccess'), 'success');

      onImport(fileName, transactions, categories, responsibles, destinations);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import CSV file');
      setIsErrorDialogOpen(true);

      // Show error toast notification
      showToast(t('budget.importError'), 'error');
    }

    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const baseClasses =
    'flex cursor-pointer items-center rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:bg-green-500 dark:hover:bg-green-600 dark:focus:ring-green-400';
  const labelClasses = className ? `${baseClasses} ${className}` : baseClasses;
  const inputId = `csv-upload-${Math.random().toString(36).substring(2, 9)}`;

  return (
    <>
      <div className="flex items-center">
        <input
          type="file"
          accept=".csv"
          onChange={handleImport}
          ref={fileInputRef}
          className="hidden"
          id={inputId}
        />
        <label
          htmlFor={inputId}
          data-attr="import-csv-button"
          className={labelClasses}
          title={t('settings.exportImport.import')}
        >
          <ArrowUpTrayIcon className="h-5 w-5" />
          {showText && <span className="ml-2">{t('settings.exportImport.import')}</span>}
        </label>
      </div>

      {/* Error Dialog */}
      <Dialog
        open={isErrorDialogOpen}
        onClose={() => setIsErrorDialogOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex min-h-screen items-center justify-center p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50" />

          <div className="relative mx-auto w-full max-w-lg rounded-lg bg-white shadow-xl dark:bg-gray-800">
            <div className="p-6">
              <Dialog.Title className="mb-4 text-lg font-medium text-red-600 dark:text-red-400">
                {t('errors.generic')}
              </Dialog.Title>

              <div className="mt-2 max-h-[60vh] overflow-y-auto">
                <div className="rounded-md bg-red-50 p-4 dark:bg-red-900/20">
                  <p className="whitespace-pre-wrap break-words text-sm text-red-700 dark:text-red-400">
                    {error}
                  </p>
                </div>

                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('common.note')}:
                  </h4>
                  <pre className="mt-2 overflow-x-auto rounded bg-gray-100 p-3 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    category,responsible,description,account,amount
                  </pre>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {t('common.note')}:
                    <br />
                    Salaire,Jérôme,"Salaire Janvier",Courant Jérôme,2500
                    <br />
                    Courses,Jess,"Courses Carrefour",Courant Jess,-150
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsErrorDialogOpen(false)}
                  className="rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};
